import { useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArmGlyph, IyafWordmark } from "@/components/brand/IyafMark";
import { AnimatedCounter } from "@/components/ui-iyaf/AnimatedCounter";
import { arms, stories } from "@/data/content";
import ArmsNav from "@/components/site/ArmsNav";

const social = arms.find((a) => a.key === "social");
const entStories = stories.filter((s) => s.arm === "entrepreneurship");
const empStories = stories.filter((s) => s.arm === "employment");

const ENT_METRIC_KEYS = [
  { key: "startupsAssessed", v: "555" },
  { key: "entrepreneursTrained", v: "1,000" },
  { key: "programCohorts", v: "46" },
  { key: "fundingSecured", v: "$2M+" },
  { key: "enteredAccelerators", v: "16" },
  { key: "satisfaction", v: "97%" },
];

const EMP_METRIC_KEYS = [
  { key: "participants", v: "3,000" },
  { key: "trainedLastYear", v: "150" },
  { key: "employed12mo", v: "80%" },
  { key: "employersInDb", v: "8,000" },
  { key: "industries", v: "12" },
  { key: "satisfaction", v: "89%" },
];

export default function Social() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("entrepreneurship");
  const industries = t("armPages.social.industries", { returnObjects: true });

  return (
    <div className="relative">
      <section className="pt-16 pb-12">
        <div className="container-iyaf grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <ArmGlyph color={social.color} className="w-14 h-14" />
              <div>
                <p className="small-caps text-xs text-iyaf-orange">{t("armCards.active")}</p>
                <p className="text-sm text-iyaf-ink/55">{t("armCards.social.tagline")}</p>
              </div>
            </div>
            <IyafWordmark variant="social" size="hero" className="mb-6" />
            <h1 className="font-display font-black tracking-tighter leading-[0.95] max-w-3xl text-balance text-4xl sm:text-5xl">
              <span className="font-editorial italic font-normal text-iyaf-ink/85">{t("armPages.social.heroTitle")}</span>
            </h1>
            <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
              {t("armPages.social.heroBody")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-[40px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1200"
                alt="Youth in entrepreneurship cohort"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="sticky top-16 sm:top-20 z-30 bg-iyaf-paper/95 backdrop-blur-md border-y border-black/5 py-3">
        <div className="container-iyaf flex gap-2">
          <button
            onClick={() => setTab("entrepreneurship")}
            data-testid="tab-entrepreneurship"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "entrepreneurship" ? "bg-iyaf-orange text-white" : "bg-white border border-black/10 text-iyaf-ink/70"}`}
          >
            {t("armPages.social.tabs.entrepreneurship")}
          </button>
          <button
            onClick={() => setTab("employment")}
            data-testid="tab-employment"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "employment" ? "bg-iyaf-blue-light text-white" : "bg-white border border-black/10 text-iyaf-ink/70"}`}
          >
            {t("armPages.social.tabs.employment")}
          </button>
        </div>
      </div>

      {tab === "entrepreneurship" ? (
        <section className="py-20" data-testid="social-ent-section">
          <div className="container-iyaf">
            <div className="grid lg:grid-cols-12 gap-10 items-center mb-20">
              <div className="lg:col-span-5">
                <p className="eyebrow text-iyaf-orange mb-4">{t("armPages.social.ent.modelEyebrow")}</p>
                <h2 className="font-display text-4xl font-bold leading-tight">{t("armPages.social.ent.modelTitle")}</h2>
                <p className="mt-5 text-iyaf-ink/75 leading-relaxed">{t("armPages.social.ent.modelBody")}</p>
              </div>
              <div className="lg:col-span-7">
                <div className="rounded-3xl bg-iyaf-ink text-white p-10 space-y-4">
                  <div className="rounded-full bg-white/8 border border-white/15 px-6 py-4 flex items-center justify-between">
                    <span className="text-sm text-white/70">{t("homeData.funnel.assessed")}</span>
                    <span className="font-display text-2xl font-bold">100%</span>
                  </div>
                  <div className="mx-8 rounded-full bg-iyaf-gray/30 border border-white/15 px-6 py-4 flex items-center justify-between">
                    <span className="text-sm text-white/70">{t("homeData.funnel.redirected")}</span>
                    <span className="font-display text-2xl font-bold text-iyaf-gray">97</span>
                  </div>
                  <div className="mx-20 rounded-full bg-iyaf-orange text-iyaf-ink px-6 py-4 flex items-center justify-between font-semibold">
                    <span className="text-sm">{t("homeData.funnel.intensive")}</span>
                    <span className="font-display text-2xl font-bold">3</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5 mb-20">
              {ENT_METRIC_KEYS.map((m) => (
                <div key={m.key} className="bg-white p-5">
                  <p className="font-display text-3xl font-extrabold text-iyaf-orange"><AnimatedCounter value={m.v} /></p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/55 mt-2">{t(`armPages.social.entMetrics.${m.key}`)}</p>
                </div>
              ))}
            </div>

            <p className="eyebrow text-iyaf-orange mb-3">{t("armPages.social.ent.venturesEyebrow")}</p>
            <h3 className="h2 mb-10">{t("armPages.social.ent.venturesTitle")}</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {entStories.map((s) => (
                <motion.article
                  key={s.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5 }}
                  className="group"
                  data-testid={`ent-story-${s.slug}`}
                >
                  <div className="relative overflow-hidden rounded-[40px] aspect-[3/4] mb-4">
                    <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <h4 className="font-display text-xl font-semibold">{s.name}</h4>
                  <p className="mt-2 text-sm text-iyaf-ink/65 leading-relaxed">{t(`storiesDeep.list.${s.slug}.summary`, s.summary)}</p>
                  <p className="mt-3 text-[10px] uppercase tracking-widest font-mono text-iyaf-orange">→ {t(`storiesDeep.list.${s.slug}.impact`, s.impact)}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20" data-testid="social-emp-section">
          <div className="container-iyaf">
            <div className="grid lg:grid-cols-12 gap-10 items-start mb-20">
              <div className="lg:col-span-6">
                <p className="eyebrow text-iyaf-blue-light mb-4">{t("armPages.social.emp.skillsEyebrow")}</p>
                <h2 className="font-display text-4xl font-bold leading-tight">{t("armPages.social.emp.skillsTitle")}</h2>
                <p className="mt-5 text-iyaf-ink/75 leading-relaxed">{t("armPages.social.emp.skillsBody")}</p>
              </div>
              <div className="lg:col-span-6">
                <p className="eyebrow text-iyaf-ink/55 mb-4">{t("armPages.social.emp.industriesEyebrow")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {Array.isArray(industries) && industries.map((ind) => (
                    <div key={ind} className="px-4 py-2.5 rounded-full bg-white border border-black/5 text-sm">{ind}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5 mb-20">
              {EMP_METRIC_KEYS.map((m) => (
                <div key={m.key} className="bg-white p-5">
                  <p className="font-display text-3xl font-extrabold text-iyaf-blue-light"><AnimatedCounter value={m.v} /></p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/55 mt-2">{t(`armPages.social.empMetrics.${m.key}`)}</p>
                </div>
              ))}
            </div>

            <p className="eyebrow text-iyaf-blue-light mb-3">{t("armPages.social.emp.perspectivesEyebrow")}</p>
            <h3 className="h2 mb-10">{t("armPages.social.emp.perspectivesTitle")}</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {empStories.map((s) => (
                <motion.article
                  key={s.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="p-8 rounded-3xl bg-white border border-black/5"
                  data-testid={`emp-story-${s.slug}`}
                >
                  <p className="font-display text-2xl font-bold">{s.name}</p>
                  <p className="mt-2 text-sm text-iyaf-ink/65">{t(`storiesDeep.list.${s.slug}.summary`, s.summary)}</p>
                  <blockquote className="mt-5 p-4 rounded-2xl bg-iyaf-paper relative">
                    <Quote size={14} className="absolute top-3 left-3 text-iyaf-ink/30" />
                    <p className="pl-6 italic text-sm">&ldquo;{t(`storiesDeep.list.${s.slug}.quote`, s.quote)}&rdquo;</p>
                  </blockquote>
                  <p className="mt-4 text-[11px] uppercase tracking-widest font-mono text-iyaf-blue-light">→ {t(`storiesDeep.list.${s.slug}.impact`, s.impact)}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      <ArmsNav currentKey="social" />
    </div>
  );
}
